/**
 * Employee Profile Hook
 * Works in both normal mode (with Azure AD) and test mode (without Azure AD)
 * Also supports Google OAuth login
 */
import { useQuery } from "react-query";
import { useMsal, useAccount } from "@azure/msal-react";
import { employeeService } from "../api/services";

// Helper function to decode JWT token (for Google OAuth)
const decodeJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

// Helper function to get user email from various auth sources
const getUserEmail = (msalAccount) => {
  // 1. Check for Google OAuth
  const authProvider = localStorage.getItem('authProvider');
  const googleCredential = localStorage.getItem('googleCredential');

  if (authProvider === 'google' && googleCredential) {
    const decoded = decodeJwt(googleCredential);
    if (decoded?.email) {
      console.log('📧 Using Google OAuth email:', decoded.email);
      return decoded.email.toLowerCase();
    }
  }

  // 2. Check for Azure AD / MSAL account
  if (msalAccount?.username) {
    console.log('📧 Using Azure AD email:', msalAccount.username);
    return msalAccount.username.toLowerCase();
  }

  // 3. Check sessionStorage for cached email
  const cachedEmail = sessionStorage.getItem('userEmail');
  if (cachedEmail) {
    console.log('📧 Using cached email:', cachedEmail);
    return cachedEmail.toLowerCase();
  }

  return null;
};

export default function useEmployeeProfile() {
  // Check if we're in test mode (bypass auth)
  const isBypassMode = process.env.REACT_APP_BYPASS_AUTH === 'true';

  // Check if we're authenticated via Google (ensure boolean)
  const isGoogleAuth = Boolean(
    localStorage.getItem('authProvider') === 'google' && localStorage.getItem('googleCredential')
  );

  // Get MSAL account for normal mode
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  // Determine if query should be enabled (ensure strict boolean)
  const shouldEnableQuery = Boolean(isBypassMode || isGoogleAuth || (account && account.username));

  const { isSuccess, data: employeeProfile, isLoading, error } = useQuery(
    "employeeProfile",
    async () => {
      // In test mode, use a test employee
      if (isBypassMode) {
        console.log('%c🧪 Test Mode: Using test employee', 'color: #f59e0b; font-weight: bold');

        // Try to get first employee from API
        try {
          const { data } = await employeeService.getEmployees({ $top: 1 });

          if (data.value && data.value.length > 0) {
            const employee = data.value[0];
            const employeeNo = employee.PersonnelNumber || employee.employeeNo || employee.number;

            console.log('✅ Test employee found:', employeeNo, employee.Name);

            return {
              employeeNo: employeeNo,
              displayName: employee.Name || employee.displayName || 'Test User',
              email: employee.PrimaryContactEmail || 'test@company.com',
              ...employee,
            };
          }

          // Fallback to mock data if no employees found
          console.warn('⚠️ No employees found in API, using mock data');
          return {
            employeeNo: '000001',
            displayName: 'Test User',
            email: 'test@company.com',
          };
        } catch (error) {
          console.error('❌ Error fetching test employee:', error);
          // Return mock data on error
          return {
            employeeNo: '000001',
            displayName: 'Test User',
            email: 'test@company.com',
          };
        }
      }

      // Normal mode: Get email from either Google OAuth or Azure AD
      const email = getUserEmail(account);

      if (!email) {
        console.error('❌ No email found in authentication provider');
        return {
          employeeNo: null,
          email: null,
          error: 'No email found in account',
        };
      }

      // Cache email for later use
      sessionStorage.setItem('userEmail', email);

      try {
        // Query Dynamics 365 API to find employee by email
        console.log('🔍 Looking up employee by email:', email);
        const { data } = await employeeService.getEmployeeByEmail(email);

        if (data.value && data.value.length > 0) {
          const employee = data.value[0];
          const employeeNo = employee.PersonnelNumber || employee.employeeNo || employee.number;
          console.log('✅ Employee found:', employeeNo, employee.Name);

          // Store employee data in sessionStorage
          sessionStorage.setItem('employeeNo', employeeNo);
          sessionStorage.setItem('employeeName', employee.Name || employee.displayName || employee.fullName);

          return {
            employeeNo: employeeNo,
            displayName: employee.Name || employee.displayName || employee.fullName || employee.DisplayName,
            email: employee.PrimaryContactEmail || employee.companyEmail || employee.CompanyEmail || email,
            ...employee,
          };
        }

        console.warn('⚠️ No employee found for email:', email);
        return {
          employeeNo: null,
          email: email,
        };
      } catch (error) {
        console.error('❌ Error fetching employee profile:', error);
        return {
          employeeNo: null,
          email: email,
          error: error.message,
        };
      }
    },
    {
      enabled: shouldEnableQuery, // Enable when in bypass mode, Google auth, OR when MSAL account exists
      retry: 2, // Retry twice on failure
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  if (isLoading) {
    return { isLoading: true, employeeNo: null };
  }

  if (error) {
    return { employeeNo: null, error, isLoading: false };
  }

  if (isSuccess) {
    return { ...employeeProfile, isLoading: false };
  }

  return { employeeNo: null, isLoading: false };
}
